import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, getToken, clearToken, DPP_BASE_URL } from '../dppApi';
import '../App.css';

// A user's collection: every product they currently or previously owned, split
// into Owned and Sold. For sold items a user can still view the documents the
// backend allows them (their chain up to the extent of their ownership, plus
// their own insurance).
const UserCollection = () => {
    const navigate = useNavigate();
    const token = getToken('user');
    const [items, setItems] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [docPanel, setDocPanel] = useState(null);
    const [viewPanel, setViewPanel] = useState(null);

    useEffect(() => {
        if (!token) { navigate('/user'); return; }
        apiFetch('/api/user/collection', { token })
            .then(setItems)
            .catch((err) => setError(err.message))
            .finally(() => setLoading(false));
    }, [token, navigate]);

    const logout = () => { clearToken('user'); navigate('/user'); };

    const DOC_META = {
        insurance: { label: 'Upload insurance', fileLabel: 'Insurance document', issuerLabel: 'Insurer', placeholder: "e.g. Lloyd's of London" },
        appraisal: { label: 'Upload appraisal', fileLabel: 'Appraisal document', issuerLabel: 'Appraiser', placeholder: 'e.g. GIA' },
    };

    const openDoc = (productId, docType) =>
        setDocPanel({ productId, docType, file: null, issuedBy: '', issuedDate: '', busy: false, error: null, done: false });

    const closeDoc = () => setDocPanel(null);

    const openDocs = async (product) => {
        setViewPanel({ productId: product.product_id, loading: true, docs: [], error: null });
        try {
            const docs = await apiFetch(`/api/passport/${product.qr_code_id}/private-docs`, { token });
            setViewPanel({ productId: product.product_id, loading: false, docs, error: null });
        } catch (err) {
            setViewPanel({ productId: product.product_id, loading: false, docs: [], error: err.message });
        }
    };

    const closeDocs = () => setViewPanel(null);

    const uploadDoc = async () => {
        if (!docPanel.file) return;
        setDocPanel(prev => ({ ...prev, busy: true, error: null }));
        try {
            const form = new FormData();
            form.append('document', docPanel.file);
            form.append('doc_type', docPanel.docType);
            if (docPanel.issuedBy) form.append('issued_by', docPanel.issuedBy);
            if (docPanel.issuedDate) form.append('issued_date', docPanel.issuedDate);
            await apiFetch(`/api/products/${docPanel.productId}/documents/owner`, { method: 'POST', token, body: form, isForm: true });
            setDocPanel(prev => ({ ...prev, busy: false, done: true }));
        } catch (err) {
            setDocPanel(prev => ({ ...prev, busy: false, error: err.message }));
        }
    };

    const renderCard = (p) => (
        <div className="dpp-card dpp-collection-item" key={p.product_id}>
            {p.image_url && p.image_url.startsWith('http') && (
                <img className="dpp-collection-image" src={p.image_url} alt={p.name} />
            )}
            <strong>{p.name}</strong>
            <a className="dpp-view-passport" href={`${DPP_BASE_URL}/jewellery/${p.qr_code_id}`} target="_blank" rel="noreferrer">View passport</a>

            {p.is_current_owner && Object.keys(DOC_META).map(docType => (
                <button
                    key={docType}
                    className="dpp-link-button"
                    onClick={() =>
                        docPanel?.productId === p.product_id && docPanel?.docType === docType
                            ? closeDoc()
                            : openDoc(p.product_id, docType)
                    }
                >
                    {docPanel?.productId === p.product_id && docPanel?.docType === docType
                        ? 'Cancel'
                        : DOC_META[docType].label}
                </button>
            ))}

            {docPanel?.productId === p.product_id && (
                <div className="dpp-transfer-panel">
                    {docPanel.done ? (
                        <p className="dpp-transfer-label" style={{ color: 'green' }}>
                            {DOC_META[docPanel.docType]?.label.replace('Upload', '')} uploaded.
                        </p>
                    ) : (
                        <>
                            <label className="dpp-transfer-label">{DOC_META[docPanel.docType]?.fileLabel}</label>
                            <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={e => setDocPanel(prev => ({ ...prev, file: e.target.files[0] || null }))}
                            />
                            <label className="dpp-transfer-label">{DOC_META[docPanel.docType]?.issuerLabel} (optional)</label>
                            <input
                                className="dpp-input"
                                placeholder={DOC_META[docPanel.docType]?.placeholder}
                                value={docPanel.issuedBy}
                                onChange={e => setDocPanel(prev => ({ ...prev, issuedBy: e.target.value }))}
                            />
                            <label className="dpp-transfer-label">Date (optional)</label>
                            <input
                                className="dpp-input"
                                type="date"
                                value={docPanel.issuedDate}
                                onChange={e => setDocPanel(prev => ({ ...prev, issuedDate: e.target.value }))}
                            />
                            {docPanel.error && <p className="dpp-error">{docPanel.error}</p>}
                            <button
                                className="dpp-button"
                                onClick={uploadDoc}
                                disabled={docPanel.busy || !docPanel.file}
                            >
                                {docPanel.busy ? 'Uploading…' : 'Upload'}
                            </button>
                        </>
                    )}
                </div>
            )}

            {!p.is_current_owner && (
                <button
                    className="dpp-link-button"
                    onClick={() => (viewPanel?.productId === p.product_id ? closeDocs() : openDocs(p))}
                >
                    {viewPanel?.productId === p.product_id ? 'Hide documents' : 'View my documents'}
                </button>
            )}

            {!p.is_current_owner && viewPanel?.productId === p.product_id && (
                <div className="dpp-transfer-panel">
                    {viewPanel.loading && <p className="dpp-muted">Loading…</p>}
                    {viewPanel.error && <p className="dpp-error">{viewPanel.error}</p>}
                    {!viewPanel.loading && !viewPanel.error && viewPanel.docs.length === 0 && (
                        <p className="dpp-muted">No documents.</p>
                    )}
                    {viewPanel.docs.map(d => (
                        <a
                            key={d.doc_id}
                            href={d.url}
                            target="_blank"
                            rel="noreferrer"
                            style={{ fontSize: 14, color: '#555' }}
                        >
                            {d.issued_by ? `${d.doc_type} — ${d.issued_by}` : d.doc_type}
                            {d.issued_date ? ` (${d.issued_date})` : ''}
                        </a>
                    ))}
                </div>
            )}
        </div>
    );

    const owned = items.filter(p => p.is_current_owner);
    const sold = items.filter(p => !p.is_current_owner);

    return (
        <div className="dpp-page">
            <div className="dpp-header">
                <h1>My Collection</h1>
                <button className="dpp-link-button" onClick={logout}>Log out</button>
            </div>

            {loading && <p className="dpp-muted">Loading…</p>}
            {error && <p className="dpp-error">{error}</p>}
            {!loading && !error && items.length === 0 && <p className="dpp-muted">You don't own any products yet.</p>}

            {owned.length > 0 && (
                <div className="dpp-grid">{owned.map(renderCard)}</div>
            )}
            {sold.length > 0 && (
                <>
                    <h2 className="dpp-collection-heading">Previously Owned</h2>
                    <div className="dpp-grid">{sold.map(renderCard)}</div>
                </>
            )}
        </div>
    );
};

export default UserCollection;
